/*
 * KLAIR: Rabattcodes — Anwendung verifizieren und Fehlschläge an Sentry melden
 *
 * Problem
 * -------
 * An drei Stellen werden Rabattcodes per `/discount/CODE` gesetzt:
 *
 *   1. snippets/month-package-selector.liquid  Paketrabatt 2-/3-Monate (+ Supplement-Code)
 *   2. snippets/month-package-selector.liquid  addSupplementsSilently()
 *   3. sections/flavor-order-page.liquid       Staffelrabatt 3/5/10 Packs
 *
 * `/discount/CODE` antwortet auch dann mit 200, wenn der Code NICHT greift —
 * abgelaufen, deaktiviert, Mindestbestellwert nicht erreicht, falscher Markt
 * (CH/DE), nicht kombinierbar mit einem bereits gesetzten Code, oder auf
 * Produkte beschränkt, die nicht im Warenkorb liegen. Ein `try/catch` um den
 * fetch faengt davon nichts: geworfen wird nur bei Netzwerkfehlern.
 *
 * Folge: Der Kunde legt in den Warenkorb, sieht den Rabatt nicht, und schreibt
 * eine Mail. Im Frontend war der einzige Hinweis ein `console.warn`, den niemand
 * sieht.
 *
 * Was dieser Waechter macht
 * -------------------------
 * a) Nach dem Anwenden gegen `/cart.js` pruefen, ob der Code tatsaechlich mit
 *    Wirkung im Warenkorb liegt.
 * b) Fehlschlag als Sentry-Warning melden — mit Code, Ursache, Warenkorbwert und
 *    Herkunft, damit die Meldung triagierbar ist statt nur zu existieren.
 * c) Auch den Navigations-Fall abdecken: wo per `window.location.href` auf
 *    `/discount/CODE?redirect=/cart` gesprungen wird, laesst sich vorher nichts
 *    pruefen. Die Erwartung wird deshalb im sessionStorage hinterlegt und nach
 *    dem Laden der Zielseite eingeloest.
 *
 * Bewusst NICHT gemacht
 * ---------------------
 * Kein globaler fetch-Wrapper. Ein fremder Wrapper hat im Projekt schon einmal
 * einen Fehler ueber mehrere Issues verteilt; ein eigener wuerde dieselbe
 * Unschaerfe erzeugen. Die drei Aufrufstellen rufen stattdessen explizit auf.
 *
 * Gruppierung in Sentry
 * ---------------------
 * Fingerprint ist [rabattcode-fehlgeschlagen, herkunft, status] — also ein Issue
 * je Seite und Fehlerart, nicht je Code. Der Code haengt als Tag dran und bleibt
 * filterbar. Ein Codewechsel erzeugt damit kein neues Issue.
 */

(function () {
  const SPEICHER = 'klair-rabatt-erwartung';
  const gemeldet = new Set();

  function wurzel() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async function warenkorb() {
    try {
      const antwort = await fetch(wurzel() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!antwort.ok) return null;
      return await antwort.json();
    } catch (fehler) {
      return null;
    }
  }

  /**
   * Status eines einzelnen Codes im Warenkorb.
   *
   * "abgelehnt"      Code steht gar nicht im Warenkorb — ungueltig, inaktiv,
   *                  falscher Markt, oder von einem nicht kombinierbaren Code
   *                  ueberschrieben.
   * "ohne_wirkung"   Code steht drin, rabattiert aber nichts — Mindestbetrag
   *                  nicht erreicht oder keine passenden Produkte im Warenkorb.
   * "ok"             Code liegt mit Betrag im Warenkorb.
   *
   * Hinweis zum Feld `applicable`: Es ist in der Cart-AJAX-API nicht garantiert
   * vorhanden. Nur auf `applicable === false` zu pruefen (statt auf einen
   * fehlenden Wahrheitswert) verhindert Falschmeldungen bei funktionierenden
   * Codes.
   */
  function status(code, gespeicherte) {
    const treffer = (gespeicherte || []).find(
      (d) => String(d.code || '').toUpperCase() === String(code).toUpperCase()
    );
    if (!treffer) return 'abgelehnt';
    if (treffer.applicable === false) return 'ohne_wirkung';
    if (typeof treffer.amount === 'number' && treffer.amount <= 0) return 'ohne_wirkung';
    return 'ok';
  }

  function melde(code, zustand, kontext, korb) {
    if (!window.Sentry || typeof window.Sentry.captureMessage !== 'function') return;

    const schluessel = code + '|' + zustand + '|' + (kontext.herkunft || '');
    if (gemeldet.has(schluessel)) return;
    gemeldet.add(schluessel);

    window.Sentry.captureMessage('Rabattcode nicht angewendet', {
      level: 'warning',
      fingerprint: ['rabattcode-fehlgeschlagen', kontext.herkunft || 'unbekannt', zustand],
      tags: {
        discount_code: code,
        discount_status: zustand,
        discount_source: kontext.herkunft || 'unbekannt'
      },
      extra: {
        erwartete_codes: kontext.codes || [code],
        codes_im_warenkorb: (korb && korb.discount_codes) || [],
        warenkorb_positionen: korb ? korb.item_count : null,
        warenkorb_summe: korb ? korb.total_price : null,
        waehrung: korb ? korb.currency : null,
        rabatt_summe: korb ? korb.total_discount : null,
        stufe: kontext.stufe || null,
        pfad: window.location.pathname
      }
    });
  }

  /**
   * Prueft nach dem Anwenden, ob die Codes wirklich im Warenkorb gelandet sind.
   * Aufruf aus den Sektionen/Snippets nach dem /discount/-Request.
   */
  async function pruefe(codes, kontext) {
    const liste = [].concat(codes).filter(Boolean);
    if (liste.length === 0) return;

    const korb = await warenkorb();
    if (!korb) return;

    // Leerer Warenkorb: es gibt nichts zu rabattieren, das ist kein Fehlschlag.
    if (!korb.item_count) return;

    liste.forEach((code) => {
      const zustand = status(code, korb.discount_codes);
      if (zustand !== 'ok') melde(code, zustand, { ...kontext, codes: liste }, korb);
    });
  }

  /**
   * Fuer den Fall, dass gleich per window.location.href auf /discount/…?redirect=…
   * navigiert wird: Erwartung hinterlegen, Einloesung passiert nach dem Laden.
   */
  function merkeFuerNavigation(codes, kontext) {
    try {
      sessionStorage.setItem(
        SPEICHER,
        JSON.stringify({ codes: [].concat(codes).filter(Boolean), kontext: kontext || {}, zeit: Date.now() })
      );
    } catch (fehler) {
      /* privater Modus: dann eben nur die fetch-Faelle */
    }
  }

  async function loeseNavigationEin() {
    let erwartung;
    try {
      const roh = sessionStorage.getItem(SPEICHER);
      if (!roh) return;
      sessionStorage.removeItem(SPEICHER);
      erwartung = JSON.parse(roh);
    } catch (fehler) {
      return;
    }

    // Aelter als zwei Minuten: der Besucher ist zwischendurch woanders gewesen,
    // eine Meldung waere nicht mehr eindeutig diesem Vorgang zuzuordnen.
    if (!erwartung || Date.now() - erwartung.zeit > 120000) return;

    await pruefe(erwartung.codes, { ...erwartung.kontext, ueber_navigation: true });
  }

  window.KlairRabattWaechter = { pruefe, merkeFuerNavigation, status };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loeseNavigationEin, { once: true });
  } else {
    loeseNavigationEin();
  }
})();
